import React, { useState, useEffect } from 'react'
import { api, formatDate } from '../utils/api'

const ACTIVITY_TYPE_INFO = {
  collection_booked: { icon: '📦', label: 'Collection Booked', color: '#3BB273' },
  quote_sent: { icon: '💰', label: 'Quote Sent', color: '#F59E0B' },
  quote_chased: { icon: '📧', label: 'Quote Chased', color: '#8B5CF6' },
  item_repriced: { icon: '🏷️', label: 'Item Repriced', color: '#3B82F6' },
  item_tested: { icon: '🔧', label: 'Item Tested', color: '#10B981' },
  item_received: { icon: '📥', label: 'Item Received', color: '#6366F1' },
  item_shipped: { icon: '🚚', label: 'Item Shipped', color: '#EC4899' },
  deal_rejected: { icon: '❌', label: 'Deal Rejected', color: '#EF4444' },
  deal_stage_changed: { icon: '📋', label: 'Deal Updated', color: '#64748B' },
  product_unlisted: { icon: '🔒', label: 'Product Unlisted', color: '#94A3B8' },
  label_sent: { icon: '📄', label: 'Label Sent', color: '#4D148C' },
  pinned: { icon: '📌', label: 'Item Pinned', color: '#FFD44D' },
  pin_completed: { icon: '✅', label: 'Pin Resolved', color: '#3BB273' }
}

function Activities() {
  const [activities, setActivities] = useState([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('')
  const [limit, setLimit] = useState(50)
  const [hasMore, setHasMore] = useState(true)

  useEffect(() => {
    loadActivities()
  }, [filter])

  async function loadActivities(append = false) {
    if (!append) setLoading(true)
    try {
      const offset = append ? activities.length : 0
      const params = new URLSearchParams({ limit: limit.toString(), offset: offset.toString() })
      if (filter) params.set('type', filter)

      const response = await api.get(`/api/activities?${params.toString()}`)
      const newActivities = response.activities || []

      if (append) {
        setActivities(prev => [...prev, ...newActivities])
      } else {
        setActivities(newActivities)
      }
      setHasMore(newActivities.length === limit)
    } catch (error) {
      console.error('Failed to load activities:', error)
    } finally {
      setLoading(false)
    }
  }

  function formatActivityTime(timestamp) {
    if (!timestamp) return ''
    const date = new Date(timestamp)
    return date.toLocaleString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  function getTypeInfo(type) {
    return ACTIVITY_TYPE_INFO[type] || { icon: '📌', label: type, color: '#64748B' }
  }

  const activityTypes = Object.entries(ACTIVITY_TYPE_INFO).map(([key, value]) => ({
    value: key,
    label: value.label
  }))

  return (
    <div className="activities-page">
      <header className="page-header">
        <div>
          <h1 className="page-title">Activity Log</h1>
          <p className="text-muted">All system activities and events</p>
        </div>
        <button className="btn btn-secondary" onClick={() => loadActivities()}>
          Refresh
        </button>
      </header>

      {/* Filters */}
      <div className="card" style={{ marginBottom: 'var(--space-4)' }}>
        <div style={{ display: 'flex', gap: 'var(--space-4)', alignItems: 'center', flexWrap: 'wrap' }}>
          <div className="form-group" style={{ margin: 0, minWidth: 200 }}>
            <label className="form-label">Filter by Type</label>
            <select
              className="form-input"
              value={filter}
              onChange={e => setFilter(e.target.value)}
            >
              <option value="">All Activities</option>
              {activityTypes.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </div>
          <div style={{ marginLeft: 'auto', color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>
            Showing {activities.length} activities
          </div>
        </div>
      </div>

      {/* Activities List */}
      <div className="card">
        {loading && activities.length === 0 ? (
          <div style={{ padding: 'var(--space-6)', textAlign: 'center', color: 'var(--color-text-muted)' }}>
            Loading activities...
          </div>
        ) : activities.length === 0 ? (
          <div style={{ padding: 'var(--space-6)', textAlign: 'center', color: 'var(--color-text-muted)' }}>
            No activities found
          </div>
        ) : (
          <>
            <table className="table">
              <thead>
                <tr>
                  <th style={{ width: 160 }} className="col-mobile-hide">Time</th>
                  <th style={{ width: 140 }} className="col-mobile-hide">Type</th>
                  <th>Activity</th>
                  <th style={{ width: 140 }} className="col-mobile-hide">User</th>
                </tr>
              </thead>
              <tbody>
                {activities.map(activity => {
                  const typeInfo = getTypeInfo(activity.type)
                  return (
                    <tr key={activity.id}>
                      <td className="text-muted col-mobile-hide" style={{ fontSize: '0.875rem' }}>
                        {formatActivityTime(activity.created_at)}
                      </td>
                      <td className="col-mobile-hide">
                        <span
                          className="badge"
                          style={{
                            background: `${typeInfo.color}20`,
                            color: typeInfo.color,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4
                          }}
                        >
                          <span>{typeInfo.icon}</span>
                          {typeInfo.label}
                        </span>
                      </td>
                      <td className="col-mobile-primary">
                        <div style={{ fontWeight: 500 }}>{activity.title}</div>
                        {activity.description && (
                          <div className="text-muted" style={{ fontSize: '0.875rem' }}>
                            {activity.description}
                          </div>
                        )}
                        <div className="mobile-row-meta" style={{ display: 'none' }}>
                          <span
                            className="badge"
                            style={{
                              background: `${typeInfo.color}20`,
                              color: typeInfo.color,
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 4,
                              fontSize: 10
                            }}
                          >
                            <span>{typeInfo.icon}</span>
                            {typeInfo.label}
                          </span>
                          <span>{formatActivityTime(activity.created_at)}</span>
                        </div>
                      </td>
                      <td className="text-muted col-mobile-hide" style={{ fontSize: '0.875rem' }}>
                        {activity.user_name || '-'}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>

            {hasMore && (
              <div style={{ padding: 'var(--space-4)', textAlign: 'center', borderTop: '1px solid var(--color-border)' }}>
                <button
                  className="btn btn-secondary"
                  onClick={() => loadActivities(true)}
                  disabled={loading}
                >
                  {loading ? 'Loading...' : 'Load More'}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default Activities
